/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import QrStatus from '@linc-technologies/linced-common/enums/qr-status';
import type Person from '@linc-technologies/linced-common/models/person';
import moment from 'moment';

// Proto: auth.QrCode.
export default class QrCode extends Model {
  declare [Type]: 'auth/qr-code';

  // personId is a uniquely assigned id that references a person within the
  // system.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  // status specifies the status of the QR Codes generation in order to help
  // present an end user with useful error information.
  @attr('number', { defaultValue: QrStatus.ERROR })
  declare status: EnumValue<typeof QrStatus> | 0;

  // expiresAt specifies when the QR Code is due to expire.
  @attr('number', { defaultValue: 0 })
  declare expiresAt: number;

  // mimetype contains the type of the data encoded within the data property.
  // For example "image/png".
  @attr('string', { defaultValue: '' })
  declare mimetype: string;

  // qrData contains the base64 encoded bytes of the qr code.
  // The serializer remaps this to/from `data`, which is a reserved name here.
  @attr('string')
  declare qrData: string | undefined;

  // src returns the QR Code as a data URI scheme, for embedding directly as
  // an image source within the viewport.
  @computed('mimetype', 'qrData')
  get src() {
    return `data:${this.mimetype};base64,${this.qrData}`;
  }

  // expiry returns a human formatted readable expiry time.
  @computed('expiresAt')
  get expiry() {
    return moment.unix(this.expiresAt).format('ll [at] LT');
  }
}
